/* @jsx h */
import { defineComponent, h, ref, watch, computed, type PropType } from "vue";
import { DateRange, useLocalization } from "@lemmonhq/uikit";
import {
  format,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachDayOfInterval,
  eachQuarterOfInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfQuarter,
} from "date-fns";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { Loading } from "@app/vue/components/UIComponents";
import { PAYMENT_METHODS } from "../../Overview/components/RevenueBars";
import type { RevenueDatum } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

type SeriesMap = Record<string, number[]>;

const toAmount = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const MonthlyPaymentBreakdown = defineComponent({
  name: "MonthlyPaymentBreakdown",
  props: {
    dateRange: {
      type: Object as PropType<DateRange>,
      required: true,
    },
    resolution: {
      type: String as PropType<"day" | "week" | "month" | "quarter" | "year">,
      default: "month",
    },
    revenueData: {
      type: Array as PropType<RevenueDatum[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const periods = ref<Date[]>([]);
    const seriesByMethod = ref<SeriesMap>({});

    const methodKeys = PAYMENT_METHODS.map((method) => method.key);
    const methodKeySet = new Set<string>(methodKeys);

    const periodLabels = computed(() =>
      periods.value.map((periodDate) => {
        switch (props.resolution) {
          case "day":
            return format(periodDate, "MMM d, yyyy");
          case "week":
            return format(periodDate, "'Week of' MMM d, yyyy");
          case "month":
            return format(periodDate, "MMM yyyy");
          case "quarter":
            return format(periodDate, "'Q'Q yyyy");
          default:
            return format(periodDate, "MMM yyyy");
        }
      }),
    );

    const totalsByPeriod = computed(() =>
      periodLabels.value.map((_, index) =>
        methodKeys.reduce((sum, key) => {
          const contribution = toAmount(
            seriesByMethod.value[key]?.[index] ?? 0,
          );
          return sum + contribution;
        }, 0),
      ),
    );

    const isEmpty = computed(
      () =>
        periodLabels.value.length === 0 ||
        totalsByPeriod.value.every((value) => value === 0),
    );

    const processData = () => {
      const interval = {
        start: parseISO(props.dateRange.startDate),
        end: parseISO(props.dateRange.endDate),
      };

      let intervalPeriods: Date[];
      switch (props.resolution) {
        case "quarter":
          intervalPeriods = eachQuarterOfInterval(interval);
          break;
        case "month":
          intervalPeriods = eachMonthOfInterval(interval);
          break;
        case "week":
          intervalPeriods = eachWeekOfInterval(interval, { weekStartsOn: 1 });
          break;
        case "day":
          intervalPeriods = eachDayOfInterval(interval);
          break;
        default:
          intervalPeriods = eachMonthOfInterval(interval);
          break;
      }

      if (intervalPeriods.length === 0) {
        periods.value = [];
        seriesByMethod.value = {};
        return;
      }

      const bucketedByMethod = new Map<string, Map<string, number>>();
      const ensureMethodMap = (methodKey: string) => {
        if (!bucketedByMethod.has(methodKey)) {
          bucketedByMethod.set(methodKey, new Map());
        }
        return bucketedByMethod.get(methodKey)!;
      };

      const getPeriodKey = (date: Date) => {
        switch (props.resolution) {
          case "quarter":
            return format(startOfQuarter(date), "yyyy-MM-dd");
          case "week":
            return format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
          case "month":
            return format(startOfMonth(date), "yyyy-MM-01");
          case "day":
            return format(date, "yyyy-MM-dd");
          default:
            return format(startOfMonth(date), "yyyy-MM-01");
        }
      };

      props.revenueData.forEach((row: RevenueDatum) => {
        if (!row.payment_method) return;
        const methodKey = row.payment_method.toLowerCase();
        if (!methodKeySet.has(methodKey)) return;
        const date = parseISO(row.revenue_date);
        const periodKey = getPeriodKey(date);
        const methodMap = ensureMethodMap(methodKey);
        const current = toAmount(methodMap.get(periodKey) ?? 0);
        const revenue = toAmount(row.revenue);
        methodMap.set(periodKey, current + revenue);
      });

      const map: SeriesMap = {};
      methodKeys.forEach((methodKey) => {
        const methodMap = bucketedByMethod.get(methodKey);
        map[methodKey] = intervalPeriods.map((periodDate) => {
          const key = getPeriodKey(periodDate);
          return methodMap?.get(key) ?? 0;
        });
      });

      periods.value = intervalPeriods;
      seriesByMethod.value = map;
    };

    watch(
      () => [
        props.dateRange.startDate,
        props.dateRange.endDate,
        props.resolution,
        props.revenueData,
      ],
      () => {
        processData();
      },
      { immediate: true, deep: true },
    );

    const downloadCSV = () => {
      const periodLabel =
        props.resolution === "day"
          ? "Day"
          : props.resolution === "week"
            ? "Week"
            : props.resolution === "month"
              ? "Month"
              : "Quarter";

      // Create CSV header
      const headers = [
        periodLabel,
        ...PAYMENT_METHODS.map((m) => m.label),
        "Total",
      ];
      const csvRows = [headers.join(",")];

      // Create CSV data rows
      periodLabels.value.forEach((label, index) => {
        const row = [
          `"${label}"`, // Quote the label to handle commas
          ...methodKeys.map((methodKey) => {
            const rawValue = seriesByMethod.value[methodKey]?.[index] ?? 0;
            return toAmount(rawValue).toFixed(2);
          }),
          totalsByPeriod.value[index]?.toFixed(2) ?? "0.00",
        ];
        csvRows.push(row.join(","));
      });

      // Create blob and download
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payment-breakdown-${props.resolution}-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    };

    const renderTableBody = () =>
      periodLabels.value.map((label, index) => {
        const total = totalsByPeriod.value[index] ?? 0;
        return (
          <tr key={label}>
            <td class="whitespace-nowrap font-medium text-accent">{label}</td>
            {methodKeys.map((methodKey) => {
              const method = PAYMENT_METHODS.find(
                (item) => item.key === methodKey,
              );
              const rawValue = seriesByMethod.value[methodKey]?.[index] ?? 0;
              const value = toAmount(rawValue);
              return (
                <td
                  class="text-right"
                  key={`${label}-${methodKey}`}
                  aria-label={method?.label ?? methodKey}
                >
                  {formatPriceInLocale(
                    value,
                    true,
                    2,
                    auth.user.value?.restaurant.currency || "CHF",
                  )}
                </td>
              );
            })}
            <td class="text-right font-semibold text-accent">
              {formatPriceInLocale(
                toAmount(total),
                true,
                2,
                auth.user.value?.restaurant.currency || "CHF",
              )}
            </td>
          </tr>
        );
      });

    return () => (
      <div class="space-y-4 rounded-2xl border bg-base-100 p-6 shadow-lg print:shadow-none print:border-gray-300 print:bg-white print:break-before-page">
        <div class="flex items-center justify-between gap-4 border-b pb-4">
          <div>
            <h3 class="text-lg font-semibold text-accent">
              {props.resolution === "day"
                ? "Daily"
                : props.resolution === "week"
                  ? "Weekly"
                  : props.resolution === "month"
                    ? "Monthly"
                    : "Quarterly"}{" "}
              Payment Breakdown
            </h3>
            <p class="text-sm text-base-content/70">
              Track revenue totals by payment channel within the selected range.
            </p>
          </div>
          <button
            class="btn btn-secondary btn-sm gap-2 print:hidden"
            onClick={downloadCSV}
            disabled={isEmpty.value}
          >
            <span class="material-symbols-outlined text-lg">download</span>
            {t("reports.revenue.download_csv")}
          </button>
        </div>

        <div class="relative">
          {props.loading ? (
            <div class="flex min-h-32 items-center justify-center bg-base-200/60 p-4 print:bg-white">
              <Loading />
            </div>
          ) : isEmpty.value ? (
            <div class="p-6 text-center text-sm text-base-content/60">
              No payment activity recorded for this period.
            </div>
          ) : (
            <div class="max-h-[520px] overflow-auto rounded-box border  print:max-h-none print:overflow-visible">
              <table class="table table-zebra table-pin-rows table-sm min-w-[720px]">
                <thead>
                  <tr>
                    <th class="text-left text-accent">
                      {props.resolution === "day"
                        ? "Day"
                        : props.resolution === "week"
                          ? "Week"
                          : props.resolution === "month"
                            ? "Month"
                            : "Quarter"}
                    </th>
                    {PAYMENT_METHODS.map((method) => (
                      <th key={method.key} class="text-right">
                        {method.label}
                      </th>
                    ))}
                    <th class="text-right text-accent">Total</th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  },
});

export default MonthlyPaymentBreakdown;
